import React from 'react';

/** Renders inline markdown: **bold**, *italic* */
function renderInline(text) {
  const parts = [];
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[2]) parts.push(<strong key={key++}>{match[2]}</strong>);
    else if (match[3]) parts.push(<em key={key++}>{match[3]}</em>);
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/** Parse markdown text into block tokens */
function tokenize(text) {
  const lines = text.split('\n');
  const tokens = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Headings
    if (line.startsWith('### ')) {
      tokens.push({ type: 'h3', text: line.slice(4) });
      i++;
      continue;
    }
    if (line.startsWith('## ')) {
      tokens.push({ type: 'h2', text: line.slice(3) });
      i++;
      continue;
    }
    if (line.startsWith('# ')) {
      tokens.push({ type: 'h1', text: line.slice(2) });
      i++;
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      tokens.push({ type: 'hr' });
      i++;
      continue;
    }

    // Table: collect all consecutive pipe lines
    if (line.trimStart().startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trimStart().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      // Parse header, separator, rows
      const rows = tableLines.map((l) =>
        l.split('|').slice(1, -1).map((c) => c.trim())
      );
      // Separator row: all cells match /^[-: ]+$/
      const sepIdx = rows.findIndex((r) => r.every((c) => /^[-: ]+$/.test(c)));
      const header = sepIdx > 0 ? rows.slice(0, sepIdx) : [];
      const body = sepIdx >= 0 ? rows.slice(sepIdx + 1) : rows;
      tokens.push({ type: 'table', header: header[0] || [], body });
      continue;
    }

    // Unordered list: collect consecutive list items
    if (/^(\s*[-*+] )/.test(line)) {
      const items = [];
      while (i < lines.length && /^(\s*[-*+] )/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+] /, ''));
        i++;
      }
      tokens.push({ type: 'ul', items });
      continue;
    }

    // Blank line
    if (line.trim() === '') {
      tokens.push({ type: 'blank' });
      i++;
      continue;
    }

    // Paragraph
    tokens.push({ type: 'p', text: line });
    i++;
  }

  return tokens;
}

export default function AiReportMarkdown({ text }) {
  if (!text) return null;
  const tokens = tokenize(text);

  return (
    <div className="ai-report-md">
      {tokens.map((tok, idx) => {
        switch (tok.type) {
          case 'h1':
            return <h1 key={idx} className="ai-md-h1">{renderInline(tok.text)}</h1>;
          case 'h2':
            return <h2 key={idx} className="ai-md-h2">{renderInline(tok.text)}</h2>;
          case 'h3':
            return <h3 key={idx} className="ai-md-h3">{renderInline(tok.text)}</h3>;
          case 'hr':
            return <hr key={idx} className="ai-md-hr" />;
          case 'ul':
            return (
              <ul key={idx} className="ai-md-ul">
                {tok.items.map((item, j) => (
                  <li key={j}>{renderInline(item)}</li>
                ))}
              </ul>
            );
          case 'table':
            return (
              <div key={idx} className="ai-md-table-wrap">
                <table className="ai-md-table">
                  {tok.header.length > 0 && (
                    <thead>
                      <tr>
                        {tok.header.map((cell, j) => (
                          <th key={j}>{renderInline(cell)}</th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {tok.body.map((row, j) => (
                      <tr key={j}>
                        {row.map((cell, k) => (
                          <td key={k}>{renderInline(cell)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'p':
            return <p key={idx} className="ai-md-p">{renderInline(tok.text)}</p>;
          case 'blank':
            return null;
          default:
            return null;
        }
      })}
    </div>
  );
}
